<template>
  <div class="base-filter-container card p-3 mb-3 bg-light shadow-sm">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center gap-2">
        <i class="fa fa-filter text-primary"></i>
        <h6 class="mb-0 fw-bold text-dark">Bộ lọc tìm kiếm</h6>
      </div>

      <div class="dropdown">
        <button
          class="btn btn-xs btn-outline-secondary dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
        >
          <i class="fa fa-cog me-1"></i> Cấu hình hiển thị ({{ activeVisibleFields.length }})
        </button>
        <div class="dropdown-menu dropdown-menu-end p-3" style="width: 280px; max-height: 450px; overflow-y: auto;">
          <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
            <span class="small fw-bold">Chọn trường hiển thị</span>
            <button class="btn btn-link btn-sm p-0 text-decoration-none small" @click="resetToDefaultConfig">Mặc định</button>
          </div>
          <div v-for="field in fields" :key="getFieldKey(field)" class="form-check mb-1">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`check-${screenKey}-${getFieldKey(field)}`"
              :checked="activeVisibleFields.includes(getFieldKey(field))"
              @change="toggleField(getFieldKey(field))"
            >
            <label class="form-check-label small user-select-none" :for="`check-${screenKey}-${getFieldKey(field)}`">
              {{ field.label }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <template v-for="field in fields" :key="getFieldKey(field)">
        <div
          v-if="activeVisibleFields.includes(getFieldKey(field))"
          :class="field.class || 'col-md-2 col-sm-6'"
        >
          <label class="form-label small fw-bold text-muted mb-1">{{ field.label }}</label>

          <select
            v-if="field.type === 'select'"
            v-model="filterValues[field.key]"
            class="form-select form-select-sm"
            @change="syncAndSearch"
          >
            <option value="">{{ field.placeholder || '--- Select ---' }}</option>
            <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>

          <multiselect
            v-else-if="field.type === 'multiselect'"
            v-model="filterValues[field.key]"
            :options="field.options"
            label="label"
            track-by="value"
            :placeholder="field.placeholder || 'Search...'"
            @select="syncAndSearch"
            @remove="syncAndSearch"
            select-label=""
            deselect-label=""
            selected-label=""
            :allow-empty="true"
            :show-labels="false"
            class="multiselect-sm"
          >
            <template #noResult>
              <span class="small">Không thấy</span>
            </template>
          </multiselect>

          <textarea
            v-else-if="field.type === 'textarea'"
            v-model="filterValues[field.key]"
            class="form-control form-control-sm"
            :placeholder="field.placeholder"
            rows="3"
            @input="handleInputSearch"
          ></textarea>

          <div v-else-if="field.type === 'checkbox'" class="form-check pt-1">
            <input
              type="checkbox"
              class="form-check-input"
              :id="`filter-${screenKey}-${getFieldKey(field)}`"
              v-model="filterValues[field.key]"
              @change="syncAndSearch"
            />
            <label class="form-check-label small text-muted" :for="`filter-${screenKey}-${getFieldKey(field)}`">
              {{ field.checkboxLabel || 'Có' }}
            </label>
          </div>

          <input
            v-else-if="field.type === 'date'"
            type="date"
            v-model="filterValues[field.key]"
            class="form-control form-control-sm"
            @change="syncAndSearch"
          />

          <div v-else-if="field.type === 'daterange'" class="input-group input-group-sm">
            <input type="date" v-model="filterValues[field.fromKey]" class="form-control" @change="syncAndSearch">
            <span class="input-group-text px-1">~</span>
            <input type="date" v-model="filterValues[field.toKey]" class="form-control" @change="syncAndSearch">
          </div>

          <input
            v-else
            :type="field.type || 'text'"
            v-model="filterValues[field.key]"
            :placeholder="field.placeholder"
            class="form-control form-control-sm"
            @input="onInputDebounce"
            @keyup.enter="syncAndSearch"
          />
        </div>
      </template>

      <div class="col-md-2 col-sm-6 col-12 d-flex align-items-end">
        <button class="btn btn-sm btn-primary w-100" style="height: 31px;" @click="syncAndSearch">
          <i class="fa fa-search me-1"></i> Search
        </button>
      </div>

      <div class="col-md-2 col-sm-6 col-12 d-flex align-items-end">
        <button class="btn btn-sm btn-outline-secondary w-100" style="height: 31px;" @click="internalReset">
          <i class="fa fa-refresh me-1"></i> Reset
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, watch, ref, onMounted, computed } from 'vue';

const props = defineProps({
  modelValue: { type: Object, required: true },
  fields: { type: Array, required: true },
  screenKey: { type: String, required: true }
});

const emit = defineEmits(['update:modelValue', 'search', 'reset']);

const timeout = ref(null);
const visibleFields = ref([]);

/**
 * Chỉ lấy các key thực sự tồn tại trong props.fields hiện tại.
 * Tránh việc localStorage lưu các key cũ đã bị xóa/đổi tên.
 */
const activeVisibleFields = computed(() => {
  const currentKeys = props.fields.map(f => getFieldKey(f));
  return visibleFields.value.filter(key => currentKeys.includes(key));
});

/**
 * FilterValues: Chỉ lưu trữ các giá trị thô của ô input.
 * Không chứa các biến hệ thống như page, limit, hay chuỗi search đã gộp.
 */
const filterValues = reactive({});

const getFieldKey = (f) => f.key || f.fromKey || f.toKey;

/**
 * Phần "search" ban đầu (props.modelValue.search) có thể chứa các điều kiện cố định
 * không tương ứng field filter nào, ví dụ customer/indexList.vue khởi tạo
 * search: 'role_id:2' để luôn giới hạn danh sách là customer. Vì syncAndSearch/
 * internalReset xây lại toàn bộ chuỗi search chỉ từ các field trong props.fields,
 * những phần cố định này cần được chụp lại 1 lần lúc mount và giữ nguyên qua mọi
 * lần search/reset — nếu không sẽ bị mất ngay khi người dùng tương tác bộ lọc.
 */
const fixedSearchParts = ref([]);

const captureFixedSearchParts = () => {
  const managedKeys = new Set();
  props.fields.forEach(field => {
    if (field.fromKey) managedKeys.add(field.fromKey);
    if (field.toKey) managedKeys.add(field.toKey);
    if (field.key) managedKeys.add(field.key);
  });

  fixedSearchParts.value = (props.modelValue.search || '')
    .split(';')
    .filter(Boolean)
    .filter(part => !managedKeys.has(part.split(':')[0]));
};

// Khởi tạo các key cho filterValues dựa trên props.fields
const initFilterValues = () => {
  props.fields.forEach(field => {
    if (field.type === 'daterange') {
      if (field.fromKey) filterValues[field.fromKey] = props.modelValue[field.fromKey] || '';
      if (field.toKey) filterValues[field.toKey] = props.modelValue[field.toKey] || '';
    } else if (field.type === 'checkbox') {
      filterValues[field.key] = props.modelValue[field.key] === true || props.modelValue[field.key] === 'true';
    } else {
      const key = getFieldKey(field);
      filterValues[key] = props.modelValue[key] || '';
    }
  });
};

const initVisibility = () => {
  const storageKey = `filter_pref_${props.screenKey}`;
  const saved = localStorage.getItem(storageKey);
  if (saved) {
    visibleFields.value = JSON.parse(saved);
  } else {
    const defaultShown = props.fields
      .filter(f => f.isDefault)
      .map(f => getFieldKey(f));
    visibleFields.value = defaultShown.length > 0
      ? defaultShown
      : props.fields.map(f => getFieldKey(f));
  }
};

const toggleField = (key) => {
  const index = visibleFields.value.indexOf(key);
  if (index > -1) {
    visibleFields.value.splice(index, 1);
  } else {
    visibleFields.value.push(key);
  }
  localStorage.setItem(`filter_pref_${props.screenKey}`, JSON.stringify(visibleFields.value));
};

const resetToDefaultConfig = () => {
  localStorage.removeItem(`filter_pref_${props.screenKey}`);
  initVisibility();
};

const syncAndSearch = () => {
  if (timeout.value) clearTimeout(timeout.value);

  const searchParts = [];
  const fieldParts = [];

  // Duyệt qua danh sách fields để lấy operator tương ứng với key
  props.fields.forEach(field => {
    if (field.type === 'daterange') {
      const fromKey = field.fromKey;
      const toKey = field.toKey;
      const fromVal = filterValues[fromKey];
      const toVal = filterValues[toKey];

      const op = field.operator || '=';
      if (fromVal) {
        searchParts.push(`${fromKey}:${fromVal}`);
      }
      if (toVal) {
        searchParts.push(`${toKey}:${toVal}`);
      }

      if (field.searchField) {
        if (fromVal || toVal) {
          fieldParts.push(`${field.searchField}:${op}`);
        }
      } else {
        if (fromVal) fieldParts.push(`${fromKey}:${op}`);
        if (toVal) fieldParts.push(`${toKey}:${op}`);
      }
    } else if (field.type === 'checkbox') {
      const key = field.key;
      if (filterValues[key] === true) {
        searchParts.push(`${key}:true`);
        fieldParts.push(`${key}:${field.operator || '='}`);
      }
    } else {
      const key = getFieldKey(field);
      let val = filterValues[key];

      // Nếu là multiselect thì lấy .value từ object
      if (field.type === 'multiselect' && val && typeof val === 'object') {
        val = val.value;
      }

      // Nếu là textarea, chuyển xuống dòng thành dấu phẩy
      if (field.type === 'textarea' && val) {
        val = val.split('\n').map(s => s.trim()).filter(s => s !== '').join(',');
      }

      if (val !== '' && val !== null && val !== undefined) {
        searchParts.push(`${key}:${val}`);
        const op = field.operator || '=';
        fieldParts.push(`${key}:${op}`);
      }
    }
  });

  const finalParams = {
    ...props.modelValue,
    search: [...fixedSearchParts.value, ...searchParts].join(';'),
    searchFields: fieldParts.join(';') // Field mới bạn vừa bổ sung
  };

  emit('update:modelValue', finalParams);
  emit('search');
};

const onInputDebounce = () => {
  if (timeout.value) clearTimeout(timeout.value);
  timeout.value = setTimeout(() => {
    syncAndSearch();
  }, 500);
};

const internalReset = () => {
  if (timeout.value) clearTimeout(timeout.value);

  // 1. Chỉ xóa các giá trị đang hiển thị trên các ô input của BaseFilter
  Object.keys(filterValues).forEach(key => {
    filterValues[key] = '';
  });

  // 2. Tạo object mới: Giữ nguyên y hệt props.modelValue, chỉ đè đúng trường search = ''
  // (vẫn giữ lại các điều kiện search cố định, xem fixedSearchParts ở trên)
  const resetParams = {
    ...props.modelValue,
    search: fixedSearchParts.value.join(';'),
    searchFields: ''
  };

  // 3. Cập nhật lại cho cha
  emit('update:modelValue', resetParams);

  // 4. Gọi reset để load lại data
  emit('reset');
};

// Đồng bộ ngược từ cha nếu cha thay đổi dữ liệu (ví dụ reset từ ngoài)
watch(() => props.modelValue, (newVal) => {
  props.fields.forEach(field => {
    if (field.type === 'daterange') {
      if (field.fromKey && newVal[field.fromKey] !== undefined) {
        filterValues[field.fromKey] = newVal[field.fromKey];
      }
      if (field.toKey && newVal[field.toKey] !== undefined) {
        filterValues[field.toKey] = newVal[field.toKey];
      }
    } else if (field.type === 'checkbox') {
      if (newVal[field.key] !== undefined) {
        filterValues[field.key] = newVal[field.key] === true || newVal[field.key] === 'true';
      }
    } else {
      const key = getFieldKey(field);
      if (newVal[key] !== undefined) {
        filterValues[key] = newVal[key];
      }
    }
  });
}, { deep: true });

onMounted(() => {
  captureFixedSearchParts();
  initVisibility();
  initFilterValues();
});
</script>

<style scoped>
.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}
.dropdown-menu {
  z-index: 1050;
}

/* Multiselect style for Filter - Sửa lỗi lệch chữ */
:deep(.multiselect-sm) {
  min-height: 31px;
  height: 31px;
  font-size: 13px;
}
:deep(.multiselect-sm .multiselect__tags) {
  min-height: 31px;
  height: 31px;
  padding: 0 30px 0 10px !important;
  border: 1px solid #ced4da;
  border-radius: 4px;
  display: flex !important;
  align-items: center !important;
  overflow: hidden;
  background: #fff;
}
:deep(.multiselect-sm .multiselect__single),
:deep(.multiselect-sm .multiselect__input),
:deep(.multiselect-sm .multiselect__placeholder) {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 31px !important;
  background: transparent !important;
  border: none !important;
  font-size: 13px !important;
  position: relative !important;
  display: inline-block !important;
  vertical-align: top !important;
  /* Thêm phần xử lý tên dài */
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 100% !important;
}
:deep(.multiselect-sm .multiselect__select) {
  height: 29px;
  width: 30px;
  padding: 0;
}
:deep(.multiselect-sm .multiselect__option) {
  padding: 10px 12px !important;
  font-size: 13px;
  min-height: 38px;
  display: flex;
  align-items: center;
}
:deep(.multiselect-sm .multiselect__content-wrapper) {
  z-index: 1060;
  position: absolute;
  width: 100%;
  border-radius: 0 0 4px 4px;
}
</style>